// =============================================================================
// src/features/analytics/analytics-types.ts - Contratos do Analytics Omar Aziz.
// =============================================================================

export type OmarAnalyticsSentiment = 'positive' | 'neutral' | 'negative';
export type OmarAnalyticsPeriod = 'last7' | 'last30';
export type OmarAnalyticsModuleName =
  | 'mentions'
  // 2026-09-20: a tela curada de hoje (os 7 baldes: recentes + top engajamento +
  // top comentarios + top views + top por canal) vira modulo proprio. 'mentions'
  // passa a ser a lista PAGINADA do conjunto — decisao do usuario nos comps.
  | 'destaques'
  | 'analysis'
  | 'digest'
  | 'influencers'
  | 'sites'
  | 'settings'
  | 'reports'
  | 'emails'
  | 'assistant'
  | 'comparison'
  | 'team';
export type OmarAnalyticsSortName = 'recent' | 'popular';
export type OmarAnalyticsMediaKind = 'noticia' | 'post' | 'video' | 'radio' | 'blog' | 'comentario';
export type OmarAnalyticsModuleGroup = 'monitoring' | 'output' | 'intelligence';
export type OmarAnalyticsChartMode = 'reach' | 'sentiment';
export type OmarAnalyticsChartGranularity = 'days' | 'weeks' | 'months';

export interface OmarAnalyticsModuleEntry {
  readonly moduleName: OmarAnalyticsModuleName;
  readonly iconName: string;
  readonly label: string;
  readonly groupName: OmarAnalyticsModuleGroup;
}

// Labels-CONTRATO dos KPIs cobertos pela coleta viva (reforma do briefing
// 2026-07-28): mock (analytics-mock-data), patch live (analytics-overlay) e os
// cards do Briefing casam por ESTES literais — mudar aqui muda nos três.
export const ANALYTICS_KPI_MENTIONS_LABEL = 'Mencoes rastreadas';
export const ANALYTICS_KPI_INTERACTIONS_LABEL = 'Interacoes';

export interface OmarAnalyticsKpi {
  readonly label: string;
  readonly value: string;
  readonly delta: string;
  readonly trend: 'up' | 'down' | 'flat';
  readonly context: string;
}

export interface OmarAnalyticsPlatformCount {
  readonly platform: string;
  readonly iconName: string;
  readonly count: number;
}

export interface OmarAnalyticsMention {
  readonly mentionId: string;
  readonly author: string;
  readonly authorHandle: string;
  readonly authorReach: string;
  readonly platform: string;
  readonly time: string;
  /** null = dado ainda inexistente na coleta viva (sentimento/reach/score/emocao
   * chegam no backlog F3.1/F3.2); o markup OMITE o elemento em vez de fingir. */
  readonly sentiment: OmarAnalyticsSentiment | null;
  /** 036: confiança do Jev no impacto (0–1); só item live julgado após 23/09.
   * Ausente = a tag mostra rótulo sem porcentagem (mock). */
  readonly sentimentConfidence?: number | null;
  readonly reach: string | null;
  readonly score: string | null;
  readonly interactions: string;
  readonly comments: string;
  readonly shares: string;
  /** Views formatadas (TikTok/Reels) — só itens live com views > 0; mock omite. */
  readonly views?: string | null;
  /** Personagens (atores politicos) casados na mencao — alimenta o filtro de
   * personagens do feed; vem de content_actor_match no live, mock omite. */
  readonly actors?: readonly string[];
  /** Slugs dos personagens (chave estável do filtro/drill-down). */
  readonly actorSlugs?: readonly string[];
  /** Relação ator↔conteúdo (016): publicacao | marcacao | mencao. */
  readonly relations?: readonly string[];
  /** Escopo Amazonas (migrations 017/018): 'nucleo' = cenário político do AM ou
   * ator monitorado; 'contexto' = AM sem carga política. O que é 'descartado' não
   * chega até aqui — o backend já não serve. Ausente no mock. */
  readonly relevanceVerdict?: string | null;
  /** 025: relacao com o VIP — badge "Omar" do card (publicacao_propria |
   * citacao | comentario_em_post_proprio | nenhuma). Ausente no mock. */
  readonly vipRelation?: string | null;
  /** 024: substancia do comentario (substantivo | reacao | vazio); null em post/news. */
  readonly textSubstance?: string | null;
  /** Identidade CRUA do autor (enum da plataforma + handle sem @) para o
   * bloqueio de falso positivo via blocklist; só itens live. */
  readonly rawPlatform?: string;
  readonly rawAuthorHandle?: string | null;
  /** Contexto da thread no rodapé do card (ex.: trecho do post comentado). */
  readonly contextNote?: string | null;
  readonly emotion: string | null;
  readonly title: string;
  readonly text: string;
  readonly sourceUrlLabel: string;
  readonly mediaKind: OmarAnalyticsMediaKind;
  readonly recentRank: number;
  readonly popularRank: number;
  readonly detailLead: string;
  readonly detailSummary: string;
  readonly detailRisk: string;
  readonly detailAction: string;
  readonly detailSignals: readonly string[];
  readonly recommendedActions: readonly string[];
  readonly tags: readonly string[];
}

export interface OmarAnalyticsChartPoint {
  readonly label: string;
  readonly mentions: number;
  readonly reach: number;
}

export interface OmarAnalyticsSentimentPoint {
  readonly label: string;
  readonly positive: number;
  readonly neutral: number;
  readonly negative: number;
}

export interface OmarAnalyticsChartSample {
  readonly label: string;
  readonly fullLabel: string;
  readonly mentions: number;
  readonly reach: number;
  readonly reachLabel: string;
  readonly positive: number;
  readonly neutral: number;
  readonly negative: number;
  readonly sourceLabel: string;
  readonly sampleTitle: string;
  readonly sampleText: string;
  readonly actionHint: string;
}

export interface OmarAnalyticsChartSeries {
  readonly granularity: OmarAnalyticsChartGranularity;
  readonly label: string;
  readonly points: readonly OmarAnalyticsChartSample[];
}

export interface OmarAnalyticsDirectoryMention {
  readonly mentionId: string;
  readonly dateLabel: string;
  readonly sourceName: string;
  readonly platform: string;
  readonly sentiment: OmarAnalyticsSentiment;
  readonly emotion: string;
  readonly score: string;
  readonly reach: string;
  readonly title: string;
  readonly excerpt: string;
}


// Tipos do relatorio visual BR-319 (DigestSection/Visual/CoverageSignal/Voice/
// Action/Report) REMOVIDOS em 2026-07-27/28: a aba Resumo de IA virou os Cards
// de Inteligencia Tatica (analytics-insights-types.ts) e a aba Relatorios virou
// a biblioteca real de PDFs — o leitor executivo mock saiu junto.
export interface OmarAnalyticsInfluencer {
  readonly influencerId: string;
  readonly influencerName: string;
  readonly handle: string;
  readonly location: string;
  readonly platform: string;
  /** null = sem sentimento na coleta viva (F3.1) — celula vira travessao. */
  readonly sentiment: OmarAnalyticsSentiment | null;
  readonly followers: string;
  readonly reach: string;
  readonly mentions: number;
  readonly voiceShare: string;
  readonly performance: string;
  readonly sentimentBars: readonly [number, number, number] | null;
  readonly lastMention: string;
  readonly expandedMentions: readonly OmarAnalyticsDirectoryMention[];
  /** Só no live (021): engajamento REAL da janela 30d — a tabela troca as colunas
   * demonstrativas (sentimento/desempenho/alcance/voz) por dado real. */
  readonly engagementLabel?: string;
}

export interface OmarAnalyticsSite {
  readonly siteId: string;
  readonly siteName: string;
  readonly siteKind: string;
  /** null = sem sentimento na coleta viva (F3.1) — celula vira travessao. */
  readonly sentiment: OmarAnalyticsSentiment | null;
  readonly visits: string;
  readonly mentions: number;
  readonly reach: string;
  readonly performance: string;
  readonly sentimentBars: readonly [number, number, number] | null;
  readonly lastHeadline: string;
  readonly expandedMentions: readonly OmarAnalyticsDirectoryMention[];
  /** Só no live (021): engajamento REAL 30d + escopo editorial do veículo
   * (local AM / nacional / outro estado — config.news_outlet_scope). */
  readonly engagementLabel?: string;
  readonly scopeLabel?: string;
}

export interface OmarAnalyticsComparisonMetric {
  readonly metricName: string;
  readonly currentValue: string;
  readonly previousValue: string;
  readonly deltaLabel: string;
  readonly iconName: string;
}

// OmarAnalyticsReport (pacotes mock + leitor executivo) REMOVIDO em 2026-07-28:
// a aba Relatorios virou a biblioteca REAL de PDFs da operacao (tipos abaixo).

// 'campanha' entrou em 2026-08-31: a pasta campanha/ do repo de relatorios acumulava
// Extraordinarios/Dossie/Notas desde 08/08 sem categoria correspondente na aba.
export type OmarAnalyticsReportCategoryName =
  | 'crise' | 'diario' | 'semanal' | 'mensal' | 'campanha';

/** Um PDF real da operacao, servido de public/assets/relatorios/. */
export interface OmarAnalyticsPdfReport {
  readonly pdfReportId: string;
  readonly categoryName: OmarAnalyticsReportCategoryName;
  /** Tipo editorial derivado do nome do arquivo (ex.: 'Paper diario', 'Anexo'). */
  readonly reportKindLabel: string;
  readonly reportTitle: string;
  readonly editionLabel: string;
  /** Caminho relativo servido pelo Vite (dev e dist), ex.: 'assets/relatorios/diario/x.pdf'. */
  readonly pdfAssetPath: string;
}

/** Categoria da biblioteca (pastas do repo atalaia-relatorios). */
export interface OmarAnalyticsReportCategory {
  readonly categoryName: OmarAnalyticsReportCategoryName;
  readonly categoryLabel: string;
  /** Mostrado quando a categoria ainda nao tem edicao publicada (ex.: mensal). */
  readonly emptyNote: string;
}

export interface OmarAnalyticsSecondaryModule {
  readonly moduleName: OmarAnalyticsModuleName;
  readonly eyebrow: string;
  readonly title: string;
  readonly body: string;
  readonly actionLabel: string;
}

export interface OmarAnalyticsFilterOption {
  readonly optionValue: string;
  readonly label: string;
}

export interface OmarAnalyticsFilterSets {
  readonly sentiments: readonly OmarAnalyticsFilterOption[];
  readonly emotions: readonly OmarAnalyticsFilterOption[];
  readonly languages: readonly OmarAnalyticsFilterOption[];
  readonly savedFilters: readonly OmarAnalyticsFilterOption[];
  readonly performanceBands: readonly OmarAnalyticsFilterOption[];
  readonly siteKinds: readonly OmarAnalyticsFilterOption[];
}

// --- Recortes de janela + configuração de ingestão (021, 2026-07-25) --------

/** Fatia de tema/região/canal na janela 30d — painéis "Recortes" da aba Análises. */
export interface OmarAnalyticsWindowSlice {
  readonly sliceLabel: string;
  readonly sliceDetail: string;
  readonly mentions: number;
  readonly sharePct: number;
}

/** Tema de monitoramento como o Dagster o usa (config.watch_query). */
export interface OmarAnalyticsIngestionTheme {
  readonly slug: string;
  readonly themeLabel: string;
  readonly terms: readonly string[];
  readonly hashtags: readonly string[];
  readonly platformLabels: readonly string[];
  readonly geo: string;
  readonly isActive: boolean;
  readonly searchEnabled: boolean;
  /** Queries REAIS da busca paga por plataforma (migration 008: banco vence). */
  readonly paidQueries: readonly { readonly platformLabel: string; readonly queryLabel: string }[];
}

/** Perfil monitorado com a cadência REAL da fila-mestra (ops.collection_target). */
export interface OmarAnalyticsIngestionProfile {
  readonly actorName: string;
  readonly platformLabel: string;
  readonly handle: string;
  readonly isActive: boolean;
  readonly cadenceLabel: string;
}

export interface OmarAnalyticsIngestionProfileGroup {
  readonly roleLabel: string;
  readonly profiles: readonly OmarAnalyticsIngestionProfile[];
}

export interface OmarAnalyticsIngestionCadence {
  readonly kindLabel: string;
  readonly platformLabel: string;
  readonly cadenceLabel: string;
  readonly activeTargets: number;
  readonly totalTargets: number;
}

export interface OmarAnalyticsScopeTermGroup {
  readonly kindLabel: string;
  readonly termCount: number;
  readonly sampleTerms: readonly string[];
}

export interface OmarAnalyticsOutletScopeGroup {
  readonly scopeLabel: string;
  readonly outletCount: number;
  readonly sampleOutlets: readonly string[];
}

export interface OmarAnalyticsBlockedAuthor {
  readonly platformLabel: string;
  readonly handle: string;
}

/** Espelho completo da ingestão p/ a aba Configurações (somente-leitura). */
export interface OmarAnalyticsIngestionConfig {
  readonly themes: readonly OmarAnalyticsIngestionTheme[];
  readonly profileGroups: readonly OmarAnalyticsIngestionProfileGroup[];
  readonly cadences: readonly OmarAnalyticsIngestionCadence[];
  readonly scopeTermGroups: readonly OmarAnalyticsScopeTermGroup[];
  readonly outletScopeGroups: readonly OmarAnalyticsOutletScopeGroup[];
  readonly blockedAuthors: readonly OmarAnalyticsBlockedAuthor[];
}
